import type {
  CreateInstance,
  GameInstance,
  GameSubmissionInput,
  GauntletSetup,
  InvitationPreview,
  Problem,
  Profile,
  Session,
  SubmissionReview,
} from "@bluegreygames/contracts";

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "content-type": "application/json", "x-bgg-csrf": "1" } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) throw new ApiError(await response.json() as Problem);
  return response.json() as Promise<T>;
}

const idempotency = () => crypto.randomUUID();

export const api = {
  session: () => request<Session>("/api/v1/session"),
  logout: () => request<{ logoutUrl: string }>("/auth/logout", { method: "POST", body: "{}" }),
  saveProfile: (profile: Profile) => request<Profile>("/api/v1/profile", {
    method: "PUT", body: JSON.stringify(profile),
  }),
  listInstances: () => request<{ items: GameInstance[] }>("/api/v1/instances"),
  createInstance: (input: CreateInstance) => request<GameInstance>("/api/v1/instances", {
    method: "POST",
    body: JSON.stringify(input),
    headers: { "idempotency-key": idempotency() },
  }),
  instanceSetup: (instanceId: string) => request<GauntletSetup>(`/api/v1/instances/${instanceId}/gauntlet/setup`),
  createInvitation: (instanceId: string, recipientEmail?: string) => request<{ shareUrl: string; expiresAt: string }>(
    `/api/v1/instances/${instanceId}/invitations`,
    { method: "POST", body: JSON.stringify({ recipientEmail }), headers: { "idempotency-key": idempotency() } },
  ),
  previewInvitation: (token: string) => request<InvitationPreview>("/api/v1/invitations/preview", {
    method: "POST", body: JSON.stringify({ token }),
  }),
  acceptInvitation: (token: string) => request<{ instanceId: string }>("/api/v1/invitations/accept", {
    method: "POST", body: JSON.stringify({ token }), headers: { "idempotency-key": idempotency() },
  }),
  saveSubmission: (instanceId: string, input: GameSubmissionInput) => request<GauntletSetup>(
    `/api/v1/instances/${instanceId}/gauntlet/submissions`,
    { method: "POST", body: JSON.stringify(input), headers: { "idempotency-key": idempotency() } },
  ),
  reviewSubmission: (instanceId: string, submissionId: string, input: SubmissionReview) => request<GauntletSetup>(
    `/api/v1/instances/${instanceId}/gauntlet/submissions/${submissionId}/review`,
    { method: "POST", body: JSON.stringify(input), headers: { "idempotency-key": idempotency() } },
  ),
  claimDefinition: (instanceId: string, definitionId: string) => request<GauntletSetup>(
    `/api/v1/instances/${instanceId}/gauntlet/allocation`,
    { method: "PUT", body: JSON.stringify({ definitionId }), headers: { "idempotency-key": idempotency() } },
  ),
};
